<template>
  <div class="flex-1 w-full overflow-y-auto rounded-lg bg-eblack-100/70">
    <div class="flex flex-col flex-grow h-full p-3 gap-y-3">
      <div class="flex flex-col gap-y-2">
        <div class="flex items-center justify-between w-full">
          <label for="name" class="text-sm md:text-md">
            {{ t("itinerary.form.customer", "Cliente") }}
          </label>

          <button
            type="button"
            class="px-2 py-1 text-sm text-white rounded-lg bg-egreen-400"
            @click="showModal()"
          >
            {{ t("itinerary.actions.add", "Agregar") }}
          </button>
        </div>

        <button
          data-dropdown-toggle="dropdownCustomerSearch"
          data-dropdown-placement="bottom"
          class="w-full border bg-white rounded-lg text-sm px-5 py-1.5 text-center inline-flex items-center mt-2"
          type="button"
        >
          <span class="flex-1 text-left">{{ customerSelected.name }}</span>
          <svg
            class="w-2.5 h-2.5 ms-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
        </button>

        <!-- Dropdown menu -->
        <div
          id="dropdownCustomerSearch"
          class="z-10 hidden bg-white rounded-lg shadow w-[90%]"
        >
          <div class="p-3">
            <label for="input-group-search" class="sr-only">
              {{ t("itinerary.form.search_sr", "Buscar") }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 flex items-center pointer-events-none rtl:inset-r-0 start-0 ps-3"
              >
                <svg
                  class="w-4 h-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="input-group-search"
                v-model="searchCustomer"
                @keyup="handleSearchCustomer()"
                class="block w-full p-1 text-xs text-gray-900 border border-gray-300 rounded-lg ps-10 bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('itinerary.form.customer_search', 'Busque cliente')"
              />
            </div>
          </div>
          <ul
            class="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700"
            aria-labelledby="dropdownSearchButton"
          >
            <li
              v-for="customer in customers_list"
              v-bind:key="customer.id"
              @click="setCustomer(customer)"
            >
              <div class="flex items-center rounded ps-2 hover:bg-gray-100">
                <IconUser />
                <label
                  for="checkbox-item-13"
                  class="w-full py-2 text-sm font-medium text-gray-900 rounded ms-2 dark:text-gray-300"
                  >{{ customer.name + " " + customer.last_name }}</label
                >
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex items-start justify-start">
        <button
          type="button"
          class="px-3 py-1 text-sm text-white rounded-lg bg-eblue-700"
          :class="{ 'opacity-50 cursor-not-allowed': isEditMode }"
          :disabled="isEditMode"
          @click="handleAddItem()"
        >
          {{ t("itinerary.actions.add_service", "Agregar servicio") }}
        </button>
        <span v-if="isEditMode" class="ml-2 text-xs text-gray-500">
          {{
            t(
              "itinerary.form.services_edit_notice",
              "Para modificar servicios, cancele este itinerario y cree uno nuevo",
            )
          }}
        </span>
      </div>
      <div class="flex flex-col flex-1 gap-4 p-1 overflow-y-auto">
        <ServiceItem
          v-for="(item, index) in items"
          v-bind:key="index"
          :pServiceDetail="item"
          :services="services"
          :index="index"
          :handleDeleteItem="handleDeleteItem"
          :readOnly="isEditMode"
          @get-ref-services="getRefServices"
          @set-form-dates="setFormDates"
          ref="services"
        />
      </div>
      <div class="flex-1 flex-grow-0 mt-auto">
        <div class="grid grid-cols-2 gap-4">
          <fieldset
            class="flex flex-col gap-1 p-1 border rounded-lg bg-eblack-50"
            v-if="serviceForm.itinerary_tour.length > 0"
          >
            <legend class="text-sm">
              {{ t("itinerary.form.totals_estimate", "Totales (estimado)") }}
            </legend>
            <p class="text-xs">
              {{ t("itinerary.summary.subtotal", "Subtotal") }}:
              ${{ calculateTotalSubtotal() }}
            </p>
            <p class="text-xs">
              {{ t("itinerary.summary.commission", "Comisión") }}:
              ${{ calculateTotalCommission() }}
            </p>
            <p class="text-xs">
              {{ t("itinerary.summary.extra_charges", "Cargos extra") }}:
              ${{ calculateTotalExtraCharges() }}
            </p>
            <p class="text-xs">
              {{ t("itinerary.summary.tax", "Impuesto") }}{{ getTaxRateDisplay() }}:
              ${{
                (parseFloat(serviceForm.tax_amount) || 0).toFixed(2)
              }}
            </p>
            <p class="text-xs font-semibold">
              {{ t("itinerary.summary.total", "Total") }}:
              ${{ calculateGrandTotal() }}
            </p>
          </fieldset>
          <fieldset
            class="flex flex-col gap-1 p-1 border rounded-lg bg-eblack-50"
            v-if="
              serviceForm.itinerary_tour.length > 0 && start_date && end_date
            "
          >
            <legend class="text-sm">
              {{ t("itinerary.form.dates", "Fechas") }}
            </legend>
            <p class="text-xs">
              {{ t("itinerary.form.start", "Inicio") }}:
              {{ formatDate(start_date) }}
            </p>
            <p class="text-xs">
              {{ t("itinerary.form.end", "Final") }}:
              {{ formatDate(end_date) }}
            </p>
          </fieldset>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import customerRequests from "@/services/admin/customer-service";
import { catalogApi } from "@/infrastructure/api";
import { IconUser } from "@tabler/icons-vue";
import { initFlowbite } from "flowbite";
import ServiceItem from "./ServiceItem.vue";
import { pricingService } from "@domains/itinerary/services/pricing.service";
import { normalizeItineraryState } from "@/domains/itinerary/models";
import { useItineraryI18n } from "@/domains/itinerary/composables";

export default {
  props: ["pItinerary", "initialDraft", "showCustomerModal"],
  name: "FormTab",
  setup() {
    const { t } = useItineraryI18n();
    return { t };
  },
  data() {
    return {
      searchCustomer: "",
      customers: [],
      customers_list: [],
      customerSelected: {
        name: "",
        id: 0,
      },
      services: [],
      items: [],
      showCustomerDropdown: false,
      serviceForm: {
        quantity_of_participants: 0,
        customer_id: 0,
        itinerary_tour: [],
        extra_charge_total: 0,
        tax_amount: 0,
        subtotal: 0,
        total: 0,
        tags: [],
        state: "PENDING",
      },
      start_date: "",
      end_date: "",
    };
  },
  watch: {
    pItinerary: {
      handler(value) {
        this.items = [];
        this.resetForm();
        this.customerSelected = {
          name: this.t(
            "itinerary.form.customer_placeholder",
            "-- Selecciona el cliente --",
          ),
          id: 0,
        };
        if (value) {
          let dataTemp = { ...value };
          this.customerSelected = dataTemp.customer;
          this.serviceForm.customer_id = this.customerSelected.id;
          this.serviceForm.state = dataTemp.state || this.serviceForm.state;
          this.serviceForm.tags = Array.isArray(dataTemp.tags)
            ? dataTemp.tags
                .filter((tag) => Number.isInteger(Number(tag)))
                .map(Number)
            : [];

          // Handle both old (itinerary_tour) and new (slots_itineraries) response formats
          const tourItems =
            dataTemp.slots_itineraries || dataTemp.itinerary_tour || [];

          // Map items and normalize the structure
          this.items = tourItems.map((tour) => {
            // Migrate legacy flat transportation fields to custom_field_values
            let customFieldValues = tour.custom_field_values || {};
            if (
              !tour.custom_field_values ||
              Object.keys(tour.custom_field_values).length === 0
            ) {
              // Check for legacy transportation fields and migrate them
              if (
                tour.transportation_pickup_location ||
                tour.transportation_pickup_time ||
                tour.transportation_dropoff_location
              ) {
                customFieldValues = {
                  pickup_location: tour.transportation_pickup_location || "",
                  pickup_time: tour.transportation_pickup_time || "",
                  dropoff_location: tour.transportation_dropoff_location || "",
                };
              }
            }

            return {
              ...tour,
              // Normalize custom_field_values
              custom_field_values: customFieldValues,
              // Per-service notes
              notes: tour.notes || "",
              // Preserve slot ID for update payload reference
              slot_id: tour.id || null,
              // If tour doesn't have quantity_of_participants, use itinerary level
              quantity_of_participants:
                tour.quantity_of_participants ??
                dataTemp.quantity_of_participants ??
                0,
            };
          });
        }
      },
      immediate: true,
    },
    initialDraft: {
      handler(value) {
        if (!value || this.pItinerary) return;
        this.$nextTick(() => {
          this.hydrateInitialDraft(value);
        });
      },
      immediate: true,
      deep: true,
    },
    serviceForm: {
      handler() {
        this.$emit("form-updated", this.getFormData());

        if (
          this.serviceForm.itinerary_tour.length > 0 &&
          this.serviceForm.itinerary_tour[0].start_datetime &&
          this.serviceForm.itinerary_tour[0].end_datetime
        ) {
          this.setFormDates();
        }
      },
      deep: true,
    },
    items: {
      handler(newItems, oldItems) {
        if (newItems.length !== oldItems.length) {
          this.$nextTick(() => {
            this.getRefServices();
          });
        }
      },
      deep: true,
    },
  },
  components: {
    IconUser,
    ServiceItem,
  },
  computed: {
    isEditMode() {
      return !!this.pItinerary;
    },
  },
  methods: {
    getFormData() {
      // Transform itinerary_tour to booking_items for the new API structure
      // quantity_of_participants is now per-item, not global
      const bookingItems = this.serviceForm.itinerary_tour.map((tour) => ({
        ...(tour.slot_id ? { id: tour.slot_id } : {}),
        service_id: tour.service_id,
        start_datetime: tour.start_datetime,
        end_datetime: tour.end_datetime,
        quantity_of_participants: tour.quantity_of_participants || 1,
        custom_field_values: tour.custom_field_values || {},
        notes: tour.notes || undefined,
      }));

      // Destructure to exclude global quantity_of_participants from API payload
      const { quantity_of_participants, ...formDataWithoutGlobalParticipants } =
        this.serviceForm;

      return {
        ...formDataWithoutGlobalParticipants,
        services_amount: this.serviceForm.itinerary_tour.length,
        // Keep itinerary_tour for internal component use
        itinerary_tour: this.serviceForm.itinerary_tour,
        // Add booking_items for API submission (each has its own quantity_of_participants)
        booking_items: bookingItems,
      };
    },
    isFormValid() {
      return (
        this.serviceForm.customer_id > 0 &&
        this.serviceForm.itinerary_tour.length > 0
      );
    },
    resetForm() {
      this.serviceForm = {
        quantity_of_participants: 0,
        customer_id: 0,
        itinerary_tour: [],
        tags: [],
        state: "PENDING",
      };
    },
    setCustomer(customer) {
      this.customerSelected.id = customer.id;
      this.customerSelected.name = customer.name + " " + customer.last_name;
      this.serviceForm.customer_id = customer.id;
      this.closeDropdownModal();
      this.$emit("form-updated", this.getFormData());
    },
    handleSearchCustomer() {
      if (this.searchCustomer != "") {
        this.customers_list = this.customers.filter((customer) =>
          customer.name
            .toLowerCase()
            .includes(this.searchCustomer.toLowerCase()),
        );
      } else {
        this.customers_list = this.customers;
      }
    },
    async initUI() {
      await Promise.all([this.getCustomers(), this.getServices()]);
      if (this.initialDraft && !this.pItinerary) {
        this.hydrateInitialDraft(this.initialDraft);
      }
    },
    getCustomers() {
      return customerRequests
        .getAll()
        .then((res) => {
          if (res.status == 200) {
            let { data } = res;
            this.customers = data;
            this.customers_list = data;
          }
        })
        .catch((error) => {
          console.log(error);
          return [];
        });
    },
    getServices() {
      return catalogApi
        .getServices()
        .then((res) => {
          if (res.status == 200) {
            let { data } = res;
            this.services = data;
          }
        })
        .catch((error) => {
          console.log(error);
          return [];
        });
    },
    hydrateInitialDraft(draft) {
      if (!draft || this.pItinerary) return;

      this.items = [];
      this.resetForm();

      const customerId = Number(draft.customer_id || draft.customer?.id || 0);
      const customer = this.customers.find((item) => Number(item.id) === customerId);
      if (customer) {
        this.customerSelected = {
          ...customer,
          name: [customer.name, customer.last_name].filter(Boolean).join(" "),
        };
      } else if (customerId > 0) {
        this.customerSelected = {
          id: customerId,
          name: this.t(
            "itinerary.form.customer_fallback",
            { id: customerId },
            "Cliente #{id}",
          ),
          last_name: "",
        };
      } else if (draft.customer_candidate?.full_name) {
        this.customerSelected = {
          id: 0,
          name: draft.customer_candidate.full_name,
          last_name: "",
        };
      }
      this.serviceForm.customer_id = customerId;
      this.serviceForm.state = normalizeItineraryState(draft.state);
      this.serviceForm.tags = Array.isArray(draft.tags)
        ? draft.tags.filter((tag) => Number.isInteger(Number(tag))).map(Number)
        : [];

      const draftItems = draft.booking_items || draft.slots_itineraries || [];
      this.items = draftItems.map((item) => this.normalizeInitialDraftItem(item));

      this.$nextTick(() => {
        initFlowbite();
        this.getRefServices();
        this.$emit("form-updated", this.getFormData());
      });
    },
    normalizeInitialDraftItem(item) {
      const serviceId = Number(item.service_id || item.service?.id || 0);
      const catalogService = this.services.find(
        (service) => Number(service.id) === serviceId,
      );
      const service = item.service ||
        catalogService || {
          id: serviceId,
          name:
            item.service_name ||
            this.t(
              "itinerary.form.service_fallback",
              { id: serviceId },
              "Servicio {id}",
            ),
        };

      return {
        ...item,
        id: item.id || null,
        service_id: serviceId,
        service,
        service_name: service.name,
        start_datetime: item.start_datetime || "",
        end_datetime: item.end_datetime || "",
        quantity_of_participants: Number(item.quantity_of_participants || 0),
        custom_field_values: item.custom_field_values || {},
        notes: item.notes || "",
      };
    },
    handleAddItem() {
      this.items.push(1);
    },
    handleDeleteItem(index) {
      this.items.splice(index, 1);
      this.serviceForm.itinerary_tour.splice(index, 1);
      this.calculateTotals();

      this.$nextTick(() => {
        this.getRefServices();
      });
    },
    calculateTotals() {
      this.serviceForm.quantity_of_participants = 0;
      this.serviceForm.services_amount = 0;

      const tours = this.serviceForm.itinerary_tour;

      if (tours.length === 0) {
        this.serviceForm.commission_amount = 0;
          this.serviceForm.tax_amount = 0;
          this.serviceForm.extra_charge_total = 0;
          this.serviceForm.subtotal = 0;
        this.serviceForm.total = 0;
        this.$emit("form-updated", this.getFormData());
        return;
      }

      let subtotal = 0;
      let commission = 0;
      let extraCharges = 0;
      let tax = 0;

      for (const tour of tours) {
        const tourSubtotal = parseFloat(tour.subtotal || 0);
        const tourCommission = parseFloat(tour.commission_total_value || 0);
        const tourExtraCharges = parseFloat(tour.extra_charge_total || 0);
        subtotal += tourSubtotal;
        commission += tourCommission;
        extraCharges += tourExtraCharges;

        if (tour.tax_amount != null) {
          tax += parseFloat(tour.tax_amount);
        } else {
          tax +=
            (tourSubtotal + tourCommission) * parseFloat(tour.tax_rate || 0.13);
        }

        this.serviceForm.services_amount += parseInt(tour.services_amount || 0);
        this.serviceForm.quantity_of_participants += parseInt(
          tour.quantity_of_participants || 0,
        );
      }

      this.serviceForm.commission_amount = pricingService
        .round(commission)
        .toFixed(2);
      this.serviceForm.extra_charge_total = pricingService
        .round(extraCharges)
        .toFixed(2);
      this.serviceForm.tax_amount = pricingService.round(tax).toFixed(2);
      this.serviceForm.subtotal = pricingService.round(subtotal).toFixed(2);
      this.serviceForm.total = pricingService
        .round(subtotal + commission + tax)
        .toFixed(2);

      this.$emit("form-updated", this.getFormData());
    },
    getRefServices() {
      let servicesRefForms = [];

      this.$nextTick(() => {
        servicesRefForms = this.$refs.services;

        if (!servicesRefForms || servicesRefForms.length === 0) {
          this.toast.error(
            this.t(
              "itinerary.form.services_processing_error",
              "Error al procesar los servicios. Intente agregar los servicios nuevamente",
            ),
            {
              timeout: 3000,
              position: "top-right",
            },
          );
          return;
        }

        let forms = servicesRefForms
          .filter((item) => item && item.serviceDetails)
          .map((item) => {
            return item.serviceDetails;
          });

        this.serviceForm.itinerary_tour = [];

        if (forms.length > 0) {
          this.serviceForm.itinerary_tour = forms;
        }
        this.calculateTotals();
      });
    },
    formatDate(dateString) {
      if (dateString != "" && dateString) {
        const parts = dateString.split("T");
        const datePart = parts[0];
        const timePart = parts[1].split(".")[0];

        const [hours24, minutes] = timePart.split(":");
        let hours = parseInt(hours24);
        const mins = minutes;

        const isAM = hours < 12;
        if (hours > 12) {
          hours -= 12;
        } else if (hours === 0) {
          hours = 12;
        }

        const formattedTime = `${hours}:${mins}${isAM ? "am" : "pm"}`;

        return `${datePart} ${formattedTime}`;
      }
      return dateString;
    },
    setFormDates() {
      this.start_date = "";
      this.end_date = "";

      this.serviceForm.itinerary_tour.forEach((tour) => {
        let tour_start_datetime = tour.start_datetime;
        let tour_end_datetime = tour.end_datetime;

        if (!tour_start_datetime || !tour_end_datetime) {
          return;
        }

        if (this.start_date == "") {
          this.start_date = tour_start_datetime;
        } else {
          let startItinerary = new Date(tour_start_datetime);
          let startForm = new Date(this.start_date);

          if (startForm > startItinerary) {
            this.start_date = tour_start_datetime;
          }
        }

        if (this.end_date == "") {
          this.end_date = tour_end_datetime;
        } else {
          let endItinerary = new Date(tour_end_datetime);
          let endForm = new Date(this.end_date);

          if (endForm < endItinerary) {
            this.end_date = tour_end_datetime;
          }
        }
      });
    },
    showModal() {
      this.showCustomerModal(false);
    },
    addNewCustomer(customer) {
      this.getCustomers();
      this.setCustomer(customer);
    },
    closeDropdownModal() {
      // Note: This uses Flowbite's dropdown which manages its own state via data-dropdown-toggle
      // Using Flowbite's API would be better, but for now we use DOM as a workaround
      // TODO: Refactor to use Flowbite's Dropdown API or migrate to Vue-based dropdown
      const element = document.getElementById("dropdownCustomerSearch");
      if (element) {
        element.classList.add("hidden");
      }
    },
    calculateTotalSubtotal() {
      return this.serviceForm.itinerary_tour
        .reduce((total, tour) => total + (parseFloat(tour.subtotal) || 0), 0)
        .toFixed(2);
    },
    calculateTotalCommission() {
      return this.serviceForm.itinerary_tour
        .reduce(
          (total, tour) =>
            total + (parseFloat(tour.commission_total_value) || 0),
          0,
        )
        .toFixed(2);
    },
    calculateTotalExtraCharges() {
      return this.serviceForm.itinerary_tour
        .reduce(
          (total, tour) => total + (parseFloat(tour.extra_charge_total) || 0),
          0,
        )
        .toFixed(2);
    },
    calculateGrandTotal() {
      return this.serviceForm.itinerary_tour
        .reduce((total, tour) => total + (parseFloat(tour.total) || 0), 0)
        .toFixed(2);
    },
    getTaxRateDisplay() {
      const tours = this.serviceForm.itinerary_tour;
      if (tours.length === 0) return "";

      const rates = [
        ...new Set(tours.map((t) => parseFloat(t.tax_rate || 0.13))),
      ];
      return rates.length === 1
        ? ` (${(rates[0] * 100).toFixed(0)}%)`
        : ` (${this.t("itinerary.form.tax_variable", "variable")})`;
    },
  },
  mounted() {
    this.initUI();
    initFlowbite();

    this.$nextTick(() => {
      this.$emit("form-updated", this.getFormData());
    });
  },
};
</script>

<style lang="scss" scoped></style>
